import {inject, Injectable, signal} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {delay, expand, forkJoin, map, Observable, of, reduce, switchMap} from 'rxjs';
import {
	MergeReceivedDTO,
	Ticket,
	TicketAssigneeRequest,
	TicketCategory,
	TicketDashboardStat,
	TicketPriority,
	TicketStatus
} from '../models/ticket.model';
import {MOCK_TICKETS} from '../mockTickets';
import {environment} from '../../../../environments/environment';
import {GatewayResponse} from '../../../core/auth/api/auth-api.service';
import {unwrapGateway} from '../../../shared/utils/gateway.utils';
import {TicketEvent} from '../models/ticket-event.model';
import {TicketEmailVM} from '../components/emails/emails.model';
import {SafeHtml} from '@angular/platform-browser';

@Injectable({
	providedIn: 'root'
})
export class TicketsService {
	private httpClient = inject(HttpClient);

	private ticketList = signal<Ticket[]>(MOCK_TICKETS);

	loadTicketsPage(options: {
		page?: number;
		pageSize?: number;
		category?: TicketCategory;
		status?: TicketStatus;
	}): Observable<GatewayResponse<Ticket>> {
		let params = new HttpParams()
			.set('page', String(options.page ?? 0))
			.set('pageSize', String(options.pageSize ?? 50));

		if (options.category) {
			params = params.set('category', options.category);
		}

		if (options.status) {
			params = params.set('status', options.status);
		}

		return this.httpClient.get<GatewayResponse<Ticket>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets`,
			{ params }
		);
	}

	loadDashboardTickets(): Observable<Ticket[]> {
		const activeStatuses: TicketStatus[] = ['TO_DO', 'ASSIGNED', 'IN_PROGRESS', 'PENDING'];

		return forkJoin(
			activeStatuses.map(status =>
				this.loadTicketPages({
					category: 'SUPPORT',
					status,
					pageSize: 100
				})
			)
		).pipe(
			map(ticketPages => ticketPages.flat()),
			switchMap(activeSupportTickets =>
				this.loadTicketsPage({
					category: 'NOT_CATEGORIZED',
					pageSize: 5
				}).pipe(
					map(res => [
						...activeSupportTickets,
						...res.data
					])
				)
			)
		);
	}

	loadDashboardCategorizationTickets(): Observable<Ticket[]> {
		return this.loadTicketsPage({
			category: 'NOT_CATEGORIZED',
			pageSize: 5
		}).pipe(
			map(res => res.data)
		);
	}

	loadTicketsByCategory(category: TicketCategory): Observable<Ticket[]> {
		if (category !== 'SUPPORT') {
			return this.loadTicketPages({
				category,
				pageSize: 50
			});
		}

		const boardStatuses: TicketStatus[] = ['TO_DO', 'ASSIGNED', 'IN_PROGRESS', 'PENDING', 'CLOSED'];

		return forkJoin(
			boardStatuses.map(status =>
				this.loadTicketPages({
					category,
					status,
					pageSize: 50
				})
			)
		).pipe(
			map(ticketPages => ticketPages.flat())
		);
	}

	private loadTicketPages(options: {
		pageSize: number;
		category: TicketCategory;
		status?: TicketStatus;
	}): Observable<Ticket[]> {
		return this.loadTicketsPage({
			...options,
			page: 0
		}).pipe(
			expand(res => {
				const currentPage = res.pagination?.page ?? 0;
				const totalPages = res.pagination?.total_page ?? 1;
				const nextPage = currentPage + 1;

				if (nextPage >= totalPages) return of();

				return this.loadTicketsPage({
					...options,
					page: nextPage
				});
			}),
			reduce((tickets, res) => [
				...tickets,
				...res.data
			], [] as Ticket[])
		);
	}

	loadDashboardStats(): Observable<TicketDashboardStat[]> {
		return this.httpClient.get<GatewayResponse<TicketDashboardStat>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/dashboard-stats`
		).pipe( unwrapGateway<TicketDashboardStat>() )
	}

	loadAssigneeRequests(): Observable<TicketAssigneeRequest[]> {
		return this.httpClient.get<GatewayResponse<TicketAssigneeRequest>>(
			`${environment.api.baseUrl}/tickets/api/v1/events/assignment-requests`
		).pipe( unwrapGateway<TicketAssigneeRequest>() )
	}

	getTicket(ticketId: string): Observable<Ticket> {
		return this.httpClient.get<GatewayResponse<Ticket>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}`
		).pipe(
			unwrapGateway<Ticket>(),
			map( tickets => tickets[0])
		)
	}

	loadTicketEvents(ticketId: string): Observable<TicketEvent[]> {
		return this.httpClient.get<GatewayResponse<TicketEvent>>(
			`${environment.api.baseUrl}/tickets/api/v1/events/ticket/${ticketId}`
		).pipe( unwrapGateway<TicketEvent>() )
	}

	loadTicketEmails(ticketId: string): Observable<TicketEmailVM[]> {
		return this.httpClient.get<GatewayResponse<TicketEmailVM>>(
			`${environment.api.baseUrl}/tickets/api/v1/emails/ticket/${ticketId}`
		).pipe( unwrapGateway<TicketEmailVM>() )
	}

	loadTargetMergeTickets() {
		return this.httpClient.get<GatewayResponse<MergeReceivedDTO>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/merged/received`
		).pipe( unwrapGateway<MergeReceivedDTO>() )
	}

	resetUnreadEmails(ticketId: string) {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/mark-inbound-read`,
			{}
		).pipe( unwrapGateway<Event>() )
	}

	assignTicketAndMaybeUpdateStatus(
		ticketId: string,
		userId: string,
		nextStatus?: TicketStatus
	) {
		return this.assignTicketDirect(ticketId, userId).pipe(
			switchMap(() => {
				if (!nextStatus) return of(null);
				return this.setTicketStatus(ticketId, nextStatus);
			})
		);
	}

	updateTicket(
		ticketId: string,
		patch: Partial<Ticket>
	): Observable<Ticket> {
		let updatedTicket!: Ticket;

		this.ticketList.update(list =>
			list.map(t => {
				if (t.id !== ticketId) return t;

				updatedTicket = {
					...t,
					...patch
				};

				return updatedTicket;
			})
		);

		return of(updatedTicket).pipe(delay(300));
	}

	mergeTicket(ticketId: string, targetTicketId: string) {
		return this.httpClient.patch<GatewayResponse<TicketEvent>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/merge/${targetTicketId}`,
			{}
		).pipe( unwrapGateway<TicketEvent>() )
	}

	setTicketStatus(ticketId: string, status: TicketStatus) {
		return this.httpClient.patch<GatewayResponse<Event>>(
		`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/status`,
			{ new_status: status },
		).pipe( unwrapGateway<Event>() )
	}

	setTicketCategory(ticketId: string, category: TicketCategory) {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/category`,
				{ new_category: category },
		).pipe( unwrapGateway<Event>() )
	}

	setTicketPriority(ticketId: string, priority: TicketPriority) {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/priority`,
			{ new_priority: priority },
		).pipe( unwrapGateway<Event>() )
	}

	attachTag(ticketId: string, tagId: string){
		return this.httpClient.patch<GatewayResponse<TicketEvent>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/tags/add/${tagId}`,
			null
		).pipe( unwrapGateway<TicketEvent>() )
	}

	removeTag(ticketId: string, tagId: string){
		return this.httpClient.patch<GatewayResponse<TicketEvent>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/tags/remove/${tagId}`,
			null
		).pipe( unwrapGateway<TicketEvent>() )
	}

	assignTicketDirect(ticketId: string, newUserId: string, reason = '') {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/assignment/direct`,
			{
				new_assignee_user_id: newUserId,
				reason: reason
			}
		).pipe( unwrapGateway<Event>() )
	}

	sendTicketAssignmentRequest(ticketId: string, newUserId: string, reason = '') {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/assignment/request`,
			{
				requested_assignee_user_id: newUserId,
				reason: reason
			}
		).pipe( unwrapGateway<Event>() )
	}

	approveTicketAssignmentRequest(ticketId: string, requestEventId: string, newUserId: string) {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/assignment/approve`,
			{
				request_event_id: requestEventId,
				new_assignee_user_id: newUserId,
			}
		).pipe( unwrapGateway<Event>() )
	}

	rejectTicketAssignmentRequest(ticketId: string, requestEventId: string, reason = '') {
		return this.httpClient.patch<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/assignment/reject`,
			{
				request_event_id: requestEventId,
				rejection_reason: reason
			}
		).pipe( unwrapGateway<Event>() )
	}

	setTicketNote(ticketId: string, note: SafeHtml) {
		return this.httpClient.post<GatewayResponse<Event>>(
			`${environment.api.baseUrl}/tickets/api/v1/tickets/${ticketId}/notes/internal`,
			{ note: note }
		).pipe( unwrapGateway<Event>())
	}
}
